import { ChangeDetectionStrategy, Component, computed, inject, signal, viewChild } from '@angular/core';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ButtonComponent, IconComponent, OverlayComponent, TranslatePipe, translator } from '@softline/ui-core';
import { DsTabsComponent } from '@softline/ui-core/tabs';
import { PageFilterService } from '../../services/page-filter.service';
import { FilterTemplateService } from '../../../services/filter-template.service';
import { FilterTemplate } from '../../../types/filter-template';
import { PageFilterComponentBase } from './page-filter-component-base';
import { PageFilterListComponent } from './page-filter-list/page-filter-list.component';
import { PageFilterCustomComponent } from './page-filter-custom/page-filter-custom.component';
import { PageFilterTemplatesComponent } from './page-filter-templates/page-filter-templates.component';

@Component({
  selector: 'soft-page-filter',
  imports: [
    OverlayComponent,
    CdkOverlayOrigin,
    ButtonComponent,
    TranslatePipe,
    IconComponent,
    DsTabsComponent,
    PageFilterListComponent,
    PageFilterCustomComponent,
    PageFilterTemplatesComponent
  ],
  templateUrl: './page-filter.component.html',
  styleUrl: './page-filter.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageFilterComponent {
  service = inject(PageFilterService);
  readonly templateService = inject(FilterTemplateService, { optional: true });
  private t = translator()

  readonly child = viewChild(PageFilterComponentBase);
  readonly templatesOpen = signal(false);

  title = computed(() => {
    const child = this.child();
    if(child)
      return child.title();
    return this.t('#CORE_FILTER.TITLE')()
  })
  canBack = computed(() => {
    const child = this.child();
    if(child)
      return child.canBack();
    return false;
  });

  canApply = computed(() => {
    const child = this.child();
    if(child && child.apply)
      return child.canApply();
    return false;
  });

  tabsVisible = computed(() => {
    const child = this.child();
    if(child)
      return child.tabsVisible();
    return true;
  })

  readonly viewTabs = computed<
    readonly { title: string; value: 'list' | 'custom'; badge?: string | number }[]
  >(() => {
    return [
      {
        title: `${this.t('#CORE_FILTER.TABS.LIST')()}`,
        value: 'list',
      },
      {
        title: `${this.t('#CORE_FILTER.TABS.CUSTOM')()}`,
        value: 'custom'
      },
    ];
  });

  readonly templateTriggerLabel = computed(() => {
    const active = this.templateService?.displayedActive();
    return active ? active.name : `${this.t('#CORE_FILTER.TEMPLATES.TITLE')()}`;
  });

  constructor() {
    this.templateService?.load()
      .catch(e => console.error('[PageFilterComponent] load templates failed', e));
  }

  applyTemplate(template: FilterTemplate): void {
    this.templateService?.applyToDraft(template);
    this.templatesOpen.set(false);
  }

  openTemplateManagement(): void {
    this.templatesOpen.set(false);
    this.service.view.set('templates');
  }

  reset = () => {
    const child = this.child();
    if(child?.reset)
      child.reset();
    else
      this.service.resetFilter();
  }

  cancel = () => {
    this.service.opened.set(false);
  }

  apply = () => {
    const child = this.child();
    if(child?.apply)
      child.apply();
    else {
      this.service.applyFilter();
      this.service.opened.set(false);
    }
  }

  back = () => {
    const child = this.child();
    if(child?.back)
      child.back();
  }
}
